import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { map, shareReplay } from 'rxjs/operators';
import {
  AppDisplay,
  appLogoByDisplay,
  favicon,
  mappingMovinmotionApplicationToAppDisplay,
  titlePrefix,
} from './app-display.model';
import { combineLatest, Observable } from 'rxjs';
import { AuthService } from './auth.service';
import { environment, MovinmotionApplication } from '@movinmotion/conf-shared';
import { environment as appEnv } from '../../../environments/environment';
import { toSignal } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class AppDisplayService {
  private readonly title = inject(Title);
  private readonly route = inject(ActivatedRoute);
  private readonly auth = inject(AuthService);
  private static readonly filmFranceUrlChecker = new RegExp(/^\/film-france(-talents)?\//);
  private static readonly adminUrlChecker = new RegExp(/^\/(admin|admin2|admincompany)/);

  private readonly appHostToDisplay: {
    [host: string]: AppDisplay;
  } = this.computeMovinmotionApplicationHosts(environment.movinmotionAppUrls);

  private _isFftSalarie$: Observable<boolean> = this.auth.redirectUrlChanged$().pipe(
    map(redirectUrl => {
      if (appEnv.displayByAppHostEnabled) {
        const isFft =
          redirectUrl?.pathname?.match(AppDisplayService.filmFranceUrlChecker) ||
          this.display() === AppDisplay.filmFranceTalents;

        return (isFft && redirectUrl && this.appHostToDisplay?.[redirectUrl?.host] === AppDisplay.worker) ?? false;
      }
      return false;
    }),
    shareReplay(1),
  );

  private suffix = signal('');

  display = toSignal<AppDisplay, AppDisplay>(
    combineLatest([this.route.queryParamMap, this.auth.redirectUrlChanged$()]).pipe(
      map(([params, redirectUrl]) => {
        if (params.has(appEnv.queryParamsKeys.display)) {
          const display = params.get(appEnv.queryParamsKeys.display);
          if (display && Object.values(AppDisplay).includes(display as any)) {
            return this.displayRouterEmployeur(display as AppDisplay);
          }
        }
        if (appEnv.displayByAppHostEnabled) {
          if (redirectUrl?.pathname?.match(AppDisplayService.filmFranceUrlChecker)) {
            return AppDisplay.filmFranceTalents;
          }
          if (
            this.appHostToDisplay[redirectUrl?.host ?? AppDisplay.employeur] === AppDisplay.employeur &&
            redirectUrl?.pathname?.match(AppDisplayService.adminUrlChecker)
          ) {
            return AppDisplay.adminBo;
          }
          if (redirectUrl && this.appHostToDisplay?.[redirectUrl?.host]) {
            return this.displayRouterEmployeur(this.appHostToDisplay[redirectUrl.host]);
          }
        }
        return AppDisplay.employeur;
      }),
      shareReplay(1),
    ),
    { initialValue: AppDisplay.employeur },
  );

  constructor() {
    effect(() => {
      const faviconUrl: string = favicon[this.display()] ?? favicon[AppDisplay.employeur];
      const faviconElement: HTMLLinkElement | null = document.querySelector('#favicon');
      if (faviconElement) {
        faviconElement.href = faviconUrl;
      }

      this.title.setTitle(`${titlePrefix[this.display()]} -  ${this.suffix()}`);
    });
  }

  setTitleSuffix(suffix: string) {
    this.suffix.set(suffix);
  }

  appLogo = computed(() => appLogoByDisplay[this.display()]);

  isFftSalarie$ = toSignal(this._isFftSalarie$);

  private computeMovinmotionApplicationHosts(apps: { [key in MovinmotionApplication]: string }): {
    [host: string]: AppDisplay;
  } {
    const result: { [host: string]: AppDisplay } = {};
    for (const [key, value] of Object.entries(apps)) {
      try {
        const url = new URL(value);
        result[url.host] = mappingMovinmotionApplicationToAppDisplay[key as MovinmotionApplication];
      } catch (err) {
        // Noop
      }
    }
    return result;
  }

  private displayRouterEmployeur(display: AppDisplay) {
    switch (display) {
      case AppDisplay.core:
      case AppDisplay.talents:
      case AppDisplay.accounting:
      case AppDisplay.social:
        return AppDisplay.employeur;
      default:
        return display;
    }
  }
}
